import React, { useContext } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Button from "@mui/material/Button";
import { Field, FieldArray, FieldArrayRenderProps, useField } from "formik";
import { v4 as uuid } from "uuid";
import * as yup from "yup";

import { Control, Status, Upload } from "pages/eligibility/type";
import Uploader, { UploadType } from "pages/eligibility/components/context/upload";
import { keyFromIdentity } from "../helpers";
import { QuestionProps } from "../question";
import OutlinedDiv from "./outlined";

export const validator = (
  question: QuestionProps,
  _questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
): Record<string, yup.AnySchema> => {
  const key = keyFromIdentity(question.identity, parent, index);

  return {
    [key]: yup.array(yup.object()),
  };
};

const createNew = () => ({ id: null, tempID: uuid(), name: null, status: Status.None } as Upload);

type StatusProps = {
  index: number;
  remove: React.MouseEventHandler<HTMLButtonElement>;
  name: string | null;
  status: Status;
}

const FileStatus = ({ index, remove, name, status }: StatusProps) => (
  <ListItem key={index}>
    {status !== Status.Uploading && <button
      type="button"
      className="close"
      aria-label="Close"
      onClick={remove}
      style={{ marginLeft: 8 }}
    >
      <span aria-hidden="true">&times;&nbsp;</span>
    </button>}
    {status === Status.Errored ? (
      <span className="badge badge-danger">Errored</span>
    ) : (
      <span className="badge">{status === Status.Uploading ? "Uploading..." : "Uploaded"}</span>
    )}
    <span style={{
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}>&nbsp;{name}</span>
  </ListItem>
);

const renderField = (
  { id, tempID, name, status }: Upload,
  uploader: UploadType,
  index: number,
  helpers: FieldArrayRenderProps,
) => {
  const selectFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files === null) return;

    const [file] = event.target.files;

    helpers.replace(index, { id, tempID, name: file.name, status: Status.Uploading });

    try {
      const uploaded = await uploader(file);
      helpers.replace(
        index,
        { id: uploaded.id, tempID, name: file.name, status: Status.Finished },
      );
    } catch {
      helpers.replace(index, { id, tempID, name: file.name, status: Status.Errored });
    }
  };

  if (status === Status.None) {
    return (
      <ListItem key={index}>
        <label htmlFor={tempID}>
          <Field
            disabled={status !== Status.None}
            id={tempID}
            name={tempID}
            type="file"
            onChange={selectFile}
            style={{ display: "none" }}
          />
          <Button className="btn-choose" variant="outlined" component="span" > Choose File </Button>
        </label>
      </ListItem>
    );
  }

  return <FileStatus key={index} index={index} name={name} status={status}
    remove={() => helpers.remove(index)}
  />;
};

const FormQuestionUpload: Control<"FormQuestionUpload"> = (props: any) => {
  const { name } = props;
  const [field] = useField(name);

  const uploader = useContext(Uploader);
  const values = ((field.value as Upload[]) || []).concat(createNew());

  return (
    <OutlinedDiv label={props.title} fullWidth required={props.required}>
      <FieldArray
        name={name}
        render={(helpers) => (
          <List>
            {values.map((upload, index) => renderField(upload, uploader, index, helpers))}
          </List>
        )}
      />
    </OutlinedDiv>
  );
};

// @ts-ignore
FormQuestionUpload.validator = validator;
export default FormQuestionUpload;
